import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUturnLeftIcon,
  TrashIcon,
  PencilIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  PhotoIcon,
} from '@heroicons/react/24/solid';
import { useMutation, useQuery } from '@apollo/client';
import { motion } from 'motion/react';
import moment from 'moment';
import {
  CREATE_MEMORY_ENTRIES,
  MemoryEntry,
  MemoryMetadata,
} from '~/services/memory';
import { GET_AGENT_TRAINING_DATA } from '~/services';
import { useLayoutContext } from '~/components/layout/Layout';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table';
import { MemoryContentPreview } from './MemoryContentPreview';
import { MemoryEditorDialog } from './MemoryEditorDialog';
import { MemoryFilter } from './MemoryFilter';
import { DocumentUploader } from './DocumentUploader';
import { TrainingDocuments } from './TrainingDocuments';
import { DaisyUIMediaLibrary } from '~/components/screens/admin/DaisyUIMediaLibrary';

// Utility function to strip __typename fields from objects
const stripTypenameFields = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(stripTypenameFields);
  }

  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== '__typename') {
      cleaned[key] = stripTypenameFields(value);
    }
  }
  return cleaned;
};

// Re-export types for consistency
export interface Memory extends MemoryEntry {
  score?: number; // Optional score field for search results
  isDeleted?: boolean; // Optional field for soft delete functionality
}

// Updated component props interface - simplified to just pass agentId
interface MemoriesModuleProps {
  agentId: string;
}

// Pagination interface
interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  total: number;
}

// Removed old content block interfaces - now using multimodal MemoryEntryContent structure

// Helper function to safely parse dates
const formatDate = (dateValue: string | number | Date): string => {
  try {
    // Handle null/undefined
    if (!dateValue) {
      return 'Invalid Date';
    }

    // If it's already a Date object
    if (dateValue instanceof Date) {
      const momentDate = moment(dateValue);
      if (!momentDate.isValid()) {
        return 'Invalid Date';
      }
      return momentDate.format('MM/DD/YYYY');
    }

    // If it's a number (timestamp)
    if (typeof dateValue === 'number') {
      const momentDate = moment(dateValue);
      if (!momentDate.isValid()) {
        return 'Invalid Date';
      }
      return momentDate.format('MM/DD/YYYY');
    }

    // If it's a string
    if (typeof dateValue === 'string') {
      // Check if it's a Unix timestamp string (all digits)
      if (/^\d+$/.test(dateValue)) {
        // Convert timestamp string to number and create date
        const timestamp = parseInt(dateValue, 10);
        const momentDate = moment(timestamp);
        if (!momentDate.isValid()) {
          return 'Invalid Date';
        }
        return momentDate.format('MM/DD/YYYY');
      }

      // Otherwise try to parse as regular date string
      const momentDate = moment(dateValue);
      if (!momentDate.isValid()) {
        return 'Invalid Date';
      }
      return momentDate.format('MM/DD/YYYY');
    }

    // Fallback for any other type
    return 'Invalid Date';
  } catch (error) {
    console.error(
      'Error parsing date:',
      error,
      'Value:',
      dateValue,
      'Type:',
      typeof dateValue,
    );
    return 'Invalid Date';
  }
};

// Component to display metadata tags with score for search results
const MetadataTags: React.FC<{ metadata: MemoryMetadata; score?: number }> = ({
  metadata,
  score,
}) => {
  return (
    <div className="flex flex-wrap gap-1">
      {metadata.tags.map((tag, index) => (
        <span
          key={index}
          className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
        >
          {tag}
        </span>
      ))}
      <span
        className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
        title={metadata.source_url || ''}
      >
        {metadata.source_type}
      </span>
      {score !== undefined && (
        <span
          className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
          title="Relevance Score"
        >
          {(score * 100).toFixed(1)}%
        </span>
      )}
    </div>
  );
};

// Skeleton loading component
const MemorySkeleton = () => (
  <div className="animate-pulse">
    <div className="mb-2 h-4 w-3/4 rounded bg-gray-200"></div>
    <div className="mb-4 h-3 w-1/2 rounded bg-gray-200"></div>
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-4">
          <div className="flex space-x-4">
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 rounded bg-gray-200"></div>
              <div className="h-3 w-1/2 rounded bg-gray-200"></div>
            </div>
            <div className="w-32 space-y-2">
              <div className="h-3 rounded bg-gray-200"></div>
              <div className="h-3 w-2/3 rounded bg-gray-200"></div>
            </div>
            <div className="h-6 w-20 rounded bg-gray-200"></div>
            <div className="flex w-16 space-x-1">
              <div className="h-6 w-6 rounded bg-gray-200"></div>
              <div className="h-6 w-6 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const MemoriesModule = ({ agentId }: MemoriesModuleProps) => {
  const [t] = useTranslation('training_settings');
  const { showToast } = useLayoutContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    pageSize: 50,
    hasMore: false,
    total: 0,
  });

  // Component state
  const [memoryList, setMemoryList] = useState<Memory[]>([]);
  const [filteredMemories, setFilteredMemories] = useState<Memory[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);
  const [isNewEntry, setIsNewEntry] = useState(false);
  const [showDocumentUploader, setShowDocumentUploader] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [activeTab, setActiveTab] = useState<'memories' | 'documents'>(
    'memories',
  );
  const [hasData, setHasData] = useState(false);

  // Calculate offset for current page
  const offset = (currentPage - 1) * pageSize;

  // GraphQL query for training data
  const { data, loading, error, refetch } = useQuery(GET_AGENT_TRAINING_DATA, {
    variables: {
      data: {
        agent_id: agentId,
        limit: pageSize,
        offset: offset,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // ALL hooks must be declared before any conditional logic
  const [createMemoryEntries, { loading: mutationLoading }] = useMutation(
    CREATE_MEMORY_ENTRIES,
    {
      onCompleted: (data) => {
        showToast(t('update_success'));
        if (data?.create_memory_entries?.data) {
          // Use the data directly since it's already in the correct format
          const updatedMemories = data.create_memory_entries.data;
          setMemoryList(updatedMemories);
          // Clear filtered results when data is updated
          if (isFiltered) {
            setIsFiltered(false);
            setFilteredMemories([]);
          }
        }
        // Close the editor dialog after successful save
        setShowEditor(false);
        setSelectedMemory(null);
        console.log('Memory entries mutation response:', data);
        // Refetch to get the latest data and update the UI
        refetch();
      },
      onError: (error) => {
        showToast(t('update_error'));
        console.error('Memory entries mutation error:', error);
        // Don't close dialog on error so user can retry
      },
    },
  );

  // Pagination handlers
  const handlePaginationChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleFilteredResults = (memories: Memory[], searchActive: boolean) => {
    setFilteredMemories(memories);
    setIsFiltered(searchActive);
  };

  const handleClearResults = () => {
    setFilteredMemories([]);
    setIsFiltered(false);
  };

  // Use filtered memories if search is active, otherwise use original memories
  const displayMemories = isFiltered ? filteredMemories : memoryList;

  const handleEditMemory = (memory: Memory) => {
    setSelectedMemory(memory);
    setIsNewEntry(false);
    setShowEditor(true);
  };

  const handleNewMemory = () => {
    setSelectedMemory(null);
    setIsNewEntry(true);
    setShowEditor(true);
  };

  const handleDeleteMemory = (id: string) => {
    const entries = [
      {
        _id: id,
        // content: '',
        metadata: {
          tags: [],
          source: 'manual',
          source_id: '',
          source_type: 'manual',
          source_name: '',
          source_url: '',
        },
        isDeleted: true,
      },
    ];

    createMemoryEntries({
      variables: {
        data: {
          agent_id: agentId,
          entries,
        },
      },
    });
  };

  const handleRestoreMemory = (id: string) => {
    const memoryToRestore = memoryList.find((m) => m._id === id);
    if (!memoryToRestore) return;

    const entries = [
      {
        _id: id,
        content: stripTypenameFields(memoryToRestore.content),
        metadata: memoryToRestore.metadata,
        isDeleted: false,
      },
    ];

    createMemoryEntries({
      variables: {
        data: {
          agent_id: agentId,
          entries,
        },
      },
    });
  };

  const handleSaveMemory = (updatedMemory: Memory) => {
    // Auto-save immediately using the mutation
    const entries = [updatedMemory].map((item) => ({
      _id: item._id,
      content: stripTypenameFields(item.content),
      metadata: {
        tags: item.metadata.tags,
        source: item.metadata.source,
        source_id: item.metadata.source_id,
        source_name: item.metadata.source_name,
        source_type: item.metadata.source_type,
        source_url: item.metadata.source_url,
      },
      isDeleted: item.isDeleted,
    }));

    createMemoryEntries({
      variables: {
        data: {
          agent_id: agentId,
          entries,
        },
      },
    });
  };

  const handleDocumentUploadComplete = (results: any) => {
    // Refresh the memories list after successful document upload
    refetch();
    setShowDocumentUploader(false);

    // Show success message
    if (results.successful && results.successful.length > 0) {
      const totalChunks = results.successful.reduce(
        (sum: number, result: any) => sum + result.chunksCreated,
        0,
      );
      console.log(
        `Successfully processed ${results.successful.length} documents into ${totalChunks} memory entries`,
      );
    }
  };

  const handleSelectMediaFromLibrary = (media: any) => {
    try {
      const url = `${import.meta.env.VITE_API_URL}/media/${media.url}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to open media:', e);
    }
  };

  // Column definitions for React Table
  const columnHelper = createColumnHelper<Memory>();

  const columns = [
    columnHelper.accessor('content', {
      header: t('content'),
      cell: (info) => (
        <div className="min-w-0 max-w-md">
          {' '}
          {/* min-w-0 ensures proper truncation */}
          <div className="mb-2 text-sm text-gray-900">
            <MemoryContentPreview content={info.getValue()} />
          </div>
          <div className="truncate text-xs text-gray-500">
            Source: {info.row.original.metadata.source_name}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('metadata', {
      header: t('metadata'),
      cell: (info) => (
        <MetadataTags
          metadata={info.getValue()}
          score={info.row.original.score}
        />
      ),
    }),
    columnHelper.accessor('created_at', {
      header: t('created'),
      cell: (info) => (
        <div className="text-sm text-gray-500">
          {formatDate(info.getValue())}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('actions'),
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-2">
          {row.original.isDeleted ? (
            <button
              onClick={() => handleRestoreMemory(row.original._id)}
              className={`text-blue-500 hover:text-blue-700 ${
                mutationLoading ? 'cursor-not-allowed opacity-50' : ''
              }`}
              title={t('restore')}
              disabled={mutationLoading}
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
            </button>
          ) : (
            <>
              <button
                onClick={() => handleEditMemory(row.original)}
                className={`text-blue-500 hover:text-blue-700 ${
                  mutationLoading ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={t('edit')}
                disabled={mutationLoading}
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteMemory(row.original._id)}
                className={`text-red-500 hover:text-red-700 ${
                  mutationLoading ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={t('delete')}
                disabled={mutationLoading}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: displayMemories,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Update state when data changes
  useEffect(() => {
    if (data?.get_agent_training_data?.data) {
      const agentData = data.get_agent_training_data.data;
      setMemoryList(agentData.memories || []);

      // Update pagination info
      setPagination({
        currentPage,
        pageSize,
        hasMore: agentData.hasMore || false,
        total: agentData.totalMemories || 0,
      });

      // Set data available for animation
      setHasData(true);
    }
  }, [data, currentPage, pageSize]);

  // Show skeleton loading during initial load
  if (loading && !data) {
    return (
      <div className="p-4">
        <div className="mb-4 text-xl font-medium">
          {t('memories_title', 'Training Memories')}
        </div>
        <MemorySkeleton />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-4">
        <div className="mb-4 text-xl font-medium">
          {t('memories_title', 'Training Memories')}
        </div>
        <div className="flex items-center justify-center p-8">
          <div className="text-red-500">
            Error loading memories: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-4"
      ref={containerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: hasData ? 1 : 0.8,
        y: hasData ? 0 : 20,
      }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className="mb-4 text-xl font-medium">
        {t('memories_title', 'Training Memories')}
      </div>

      {/* Loading indicator for mutations */}
      {mutationLoading && (
        <div className="mb-4 flex items-center space-x-3 rounded-lg bg-blue-50 p-3">
          <progress className="progress w-56"></progress>
          <span className="text-sm text-blue-700">
            {t('saving_changes', 'Saving changes...')}
          </span>
        </div>
      )}

      {/* Filter Component */}
      <MemoryFilter
        agentId={agentId}
        onFilteredResults={handleFilteredResults}
        onClearResults={handleClearResults}
      />

      <div className="flex w-full flex-col gap-4">
        {/* Tab Navigation */}
        <div className="tabs-boxed tabs w-fit">
          <button
            className={`tab ${activeTab === 'memories' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('memories')}
          >
            {t('memories', 'Memories')}
          </button>
          <button
            className={`tab ${activeTab === 'documents' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            {t('documents', 'Documents')}
          </button>
        </div>

        {activeTab === 'memories' && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <p className="text-sm">
                  {t(
                    'memories_description',
                    "Manage your agent's training memories and knowledge base.",
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {isFiltered ? (
                    <>
                      Search results: {displayMemories.length} | Total memories:{' '}
                      {pagination?.total || 0}
                    </>
                  ) : pagination ? (
                    `${t('page_of', {
                      current: pagination.currentPage,
                      total: Math.ceil(
                        (pagination.total || 0) / pagination.pageSize,
                      ),
                    })} | ${t('pagination_showing', {
                      from:
                        (pagination.currentPage - 1) * pagination.pageSize + 1,
                      to: Math.min(
                        pagination.currentPage * pagination.pageSize,
                        pagination.total || 0,
                      ),
                      total: pagination.total || 0,
                    })}`
                  ) : (
                    <>
                      Total memories: {(pagination as any).total || 0} |
                      Displayed: {displayMemories.length}
                    </>
                  )}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  className={`btn btn-primary flex items-center rounded-md ${
                    mutationLoading ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  onClick={() => setShowDocumentUploader(true)}
                  disabled={mutationLoading}
                >
                  <DocumentArrowUpIcon className="mr-1 h-4 w-4" />
                  {t('upload_documents', 'Upload Documents')}
                </button>
                <button
                  className={`btn btn-outline flex items-center rounded-md ${
                    mutationLoading ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  onClick={() => setShowMediaLibrary(true)}
                  disabled={mutationLoading}
                >
                  <PhotoIcon className="mr-1 h-4 w-4" />
                  {t('open_media_library', 'Media Library')}
                </button>
                <button
                  className={`btn btn-primary flex items-center rounded-md ${
                    mutationLoading ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  onClick={handleNewMemory}
                  disabled={mutationLoading}
                >
                  <PlusIcon className="mr-1 h-4 w-4" />
                  {t('add_memory', 'Add Memory')}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border">
              <table className="min-w-full table-fixed divide-gray-200">
                <thead className="bg-gray-50">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${
                            index === 0
                              ? 'w-[45%]' // Content column gets more space
                              : index === 1
                                ? 'w-[30%]' // Metadata column
                                : index === 2
                                  ? 'w-[15%]' // Date column
                                  : 'w-[10%]' // Actions column
                          }`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`${
                        row.original.isDeleted
                          ? 'bg-red-50'
                          : isFiltered
                            ? 'hover:bg-blue-50' // Different hover for search results
                            : 'hover:bg-gray-50'
                      } ${mutationLoading ? 'opacity-60' : ''}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 text-sm">
                          <div className="max-h-24 overflow-y-auto">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {displayMemories.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        {isFiltered
                          ? t(
                              'no_search_results',
                              'No memories found matching your search criteria.',
                            )
                          : t(
                              'no_memories',
                              'No memories found. Add your first training memory!',
                            )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  {/* Mobile pagination */}
                  <button
                    onClick={() =>
                      handlePaginationChange(pagination.currentPage - 1)
                    }
                    disabled={pagination.currentPage <= 1 || mutationLoading}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('previous')}
                  </button>
                  <button
                    onClick={() =>
                      handlePaginationChange(pagination.currentPage + 1)
                    }
                    disabled={!pagination.hasMore || mutationLoading}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('next')}
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-700">
                      {t('pagination_showing', {
                        from:
                          (pagination.currentPage - 1) * pagination.pageSize +
                          1,
                        to: Math.min(
                          pagination.currentPage * pagination.pageSize,
                          pagination.total || 0,
                        ),
                        total: pagination.total || 0,
                      })}
                    </p>

                    {/* Page Size Selector */}
                    <div className="flex items-center space-x-2">
                      <label
                        htmlFor="pageSize"
                        className="text-sm text-gray-700"
                      >
                        {t('show')}:
                      </label>
                      <select
                        id="pageSize"
                        value={pagination.pageSize}
                        onChange={(e) =>
                          handlePageSizeChange(parseInt(e.target.value))
                        }
                        disabled={mutationLoading}
                        className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-gray-700">
                        {t('per_page')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <nav
                      className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                      aria-label="Pagination"
                    >
                      {/* Previous button */}
                      <button
                        onClick={() =>
                          handlePaginationChange(pagination.currentPage - 1)
                        }
                        disabled={
                          pagination.currentPage <= 1 || mutationLoading
                        }
                        className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="sr-only">{t('previous')}</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Page numbers */}
                      {(() => {
                        const totalPages = Math.ceil(
                          (pagination.total || 0) / pagination.pageSize,
                        );
                        const currentPage = pagination.currentPage;
                        const pages = [];

                        // Always show first page
                        if (totalPages > 0) {
                          pages.push(1);
                        }

                        // Show pages around current page
                        let start = Math.max(2, currentPage - 1);
                        let end = Math.min(totalPages - 1, currentPage + 1);

                        if (start > 2) {
                          pages.push('...');
                        }

                        for (let i = start; i <= end; i++) {
                          if (i !== 1 && i !== totalPages) {
                            pages.push(i);
                          }
                        }

                        if (end < totalPages - 1) {
                          pages.push('...');
                        }

                        // Always show last page
                        if (totalPages > 1) {
                          pages.push(totalPages);
                        }

                        return pages.map((page, index) => (
                          <button
                            key={index}
                            onClick={() =>
                              typeof page === 'number'
                                ? handlePaginationChange(page)
                                : undefined
                            }
                            disabled={
                              typeof page !== 'number' || mutationLoading
                            }
                            className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                                : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                            } ${
                              typeof page !== 'number' || mutationLoading
                                ? 'cursor-not-allowed opacity-50'
                                : ''
                            }`}
                          >
                            {page}
                          </button>
                        ));
                      })()}

                      {/* Next button */}
                      <button
                        onClick={() =>
                          handlePaginationChange(pagination.currentPage + 1)
                        }
                        disabled={!pagination.hasMore || mutationLoading}
                        className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="sr-only">{t('next')}</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'documents' && (
          <TrainingDocuments agentId={agentId} onRefresh={() => refetch()} />
        )}
      </div>

      {/* Memory Editor Dialog */}
      <MemoryEditorDialog
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        memory={selectedMemory}
        onSave={handleSaveMemory}
        agentId={agentId}
        isNew={isNewEntry}
        isSaving={mutationLoading}
      />

      {/* Document Uploader Dialog */}
      {showDocumentUploader && (
        <DocumentUploader
          agentId={agentId}
          onUploadComplete={handleDocumentUploadComplete}
          onClose={() => setShowDocumentUploader(false)}
        />
      )}

      {/* Media Library Dialog */}
      <DaisyUIMediaLibrary
        agentId={agentId}
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelectMedia={handleSelectMediaFromLibrary}
      />
    </motion.div>
  );
};

export default MemoriesModule;
